/**
 * Recipe stage (DSS Phase B): Parameters | Output | Verification
 * One primary path — finalize goes to /results (no Stage 5 theater).
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Alert,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material';
import {
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Calculate as CalculateIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import {
  WorkflowStage,
  setStockSolution,
  setStageValidation,
  setCalculationResults,
} from '../store/slices/workflowSlice';
import WorkflowLayout from '../components/workflow/WorkflowLayout';
import DssContextStrip from '../components/workflow/DssContextStrip';
import SaltCompatibilityAnalyzer from '../components/stock-solution/SaltCompatibilityAnalyzer';
import ProfessionalStockCalculator from '../components/stock-solution/ProfessionalStockCalculator';
import ABTankSeparationEngine from '../components/stock-solution/ABTankSeparationEngine';
import HendersonHasselbalchCalculator from '../components/ph-adjustment/HendersonHasselbalchCalculator';
import ProfessionalNutrientReport from '../components/reports/ProfessionalNutrientReport';
import {
  buildCalculationContext,
  contextToTargetMap,
} from '../utils/CalculationContext';
import { buildWorkflowResultsFromStage4 } from '../utils/buildWorkflowResultsFromStage4';

const DOF_OPTIONS = [
  'S',
  'P',
  'K',
  'N (NO3-)',
  'N (NH4+)',
  'Mg',
  'Ca',
  'Cu',
  'Fe',
  'Mo',
  'Si',
  'Na',
  'Cl',
  'Mn',
  'B',
  'Zn',
];

type PanelId = 'parameters' | 'output' | 'verification';

const PANELS: { id: PanelId; label: string }[] = [
  { id: 'parameters', label: 'Parameters' },
  { id: 'output', label: 'Output' },
  { id: 'verification', label: 'Verification' },
];

const StockSolutionsWorkflowPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [stockType, setStockType] = useState<'single' | 'partAB'>('partAB');
  const [stockVolume, setStockVolume] = useState(10);
  const [volumeUnit, setVolumeUnit] = useState('L');
  const [dilutionRatio, setDilutionRatio] = useState(100);
  const [concentrationFactor, setConcentrationFactor] = useState(100);
  const [workingSolutionVolume, setWorkingSolutionVolume] = useState(1000);
  const [degreeOfFreedomElement, setDegreeOfFreedomElement] = useState('S');
  const [partASelection, setPartASelection] = useState<string[]>([]);
  const [partBSelection, setPartBSelection] = useState<string[]>([]);
  const [panel, setPanel] = useState<PanelId>('parameters');
  const [compatibilityAnalysis, setCompatibilityAnalysis] = useState<any>(null);
  const [stockCalculationResult, setStockCalculationResult] = useState<any>(null);
  const [abSeparationResult, setAbSeparationResult] = useState<any>(null);
  const [phAdjustmentResult, setPhAdjustmentResult] = useState<any>(null);

  const { saltSelection, formulation, waterProfile } = useAppSelector(
    (state) => state.workflow
  );

  const calcContext = useMemo(
    () =>
      buildCalculationContext({
        waterProfile,
        formulation,
        saltSelection,
        stockVolume,
        concentrationFactor: concentrationFactor || dilutionRatio,
        degreeOfFreedomElement,
        temperatureC: 20,
      }),
    [
      waterProfile,
      formulation,
      saltSelection,
      stockVolume,
      concentrationFactor,
      dilutionRatio,
      degreeOfFreedomElement,
    ]
  );

  const configReady =
    calcContext.salts.length > 0 &&
    calcContext.formulation.elements.length > 0 &&
    stockVolume > 0 &&
    concentrationFactor >= 1 &&
    Boolean(compatibilityAnalysis);

  const handleCompatibilityAnalysis = useCallback((analysis: any) => {
    setCompatibilityAnalysis(analysis);
    setPartASelection(analysis.partA || []);
    setPartBSelection(analysis.partB || []);
    if (analysis.recommendAB) setStockType('partAB');
  }, []);

  const handleStockCalculationComplete = useCallback(
    (result: any) => {
      const tankById = compatibilityAnalysis?.tankById || {};
      const saltAmounts = (result?.saltAmounts || []).map((s: any) => ({
        ...s,
        tankAssignment:
          s.tankAssignment ||
          tankById[s.id] ||
          (stockType === 'partAB' ? 'A' : 'A'),
      }));
      setStockCalculationResult({ ...result, saltAmounts });
      setPanel('verification');
    },
    [compatibilityAnalysis, stockType]
  );

  const handleABSeparationComplete = useCallback((result: any) => {
    setAbSeparationResult(result);
    if (result?.allSalts?.length && stockCalculationResult) {
      const byId: Record<string, string> = {};
      (result.tankA?.salts || []).forEach((s: any) => {
        byId[s.id] = 'A';
      });
      (result.tankB?.salts || []).forEach((s: any) => {
        byId[s.id] = 'B';
      });
      setStockCalculationResult((prev: any) => ({
        ...prev,
        saltAmounts: (prev?.saltAmounts || []).map((s: any) => ({
          ...s,
          tankAssignment: byId[s.id] || s.tankAssignment || 'A',
        })),
      }));
    }
  }, [stockCalculationResult]);

  const handlePHAdjustmentCalculated = useCallback((result: any) => {
    setPhAdjustmentResult(result);
  }, []);

  const reportBundle = useMemo(() => {
    if (!stockCalculationResult) return null;
    return buildWorkflowResultsFromStage4({
      stockCalculationResult,
      formulation,
      waterProfile,
      stockVolume,
      dilutionRatio: concentrationFactor || dilutionRatio,
      workingSolutionVolume,
      phAdjustmentResult,
      abSeparationResult,
    });
  }, [
    stockCalculationResult,
    formulation,
    waterProfile,
    stockVolume,
    dilutionRatio,
    concentrationFactor,
    workingSolutionVolume,
    phAdjustmentResult,
    abSeparationResult,
  ]);

  const handleFinalize = () => {
    if (!stockCalculationResult || !reportBundle) return;

    const cf = concentrationFactor || dilutionRatio;
    const stockSolutionConfig = {
      type: stockType as any,
      volume: stockVolume,
      volumeUnit,
      configuration: {
        ...(stockType === 'partAB' && {
          partA: { salts: partASelection, volume: stockVolume / 2 },
          partB: { salts: partBSelection, volume: stockVolume / 2 },
        }),
        ...(stockType === 'single' && {
          single: {
            salts: [...partASelection, ...partBSelection],
            volume: stockVolume,
          },
        }),
      },
      dilutionRatio: cf,
      workingSolutionVolume,
      preparationSteps: stockCalculationResult.preparationSteps || [],
      qualityChecks: stockCalculationResult.qualityChecks || [],
      saltAmounts: stockCalculationResult.saltAmounts,
      compatibilityAnalysis,
      calculationResult: stockCalculationResult,
    } as any;

    dispatch(setStockSolution(stockSolutionConfig));
    dispatch(setCalculationResults(reportBundle.calculationResults));
    dispatch(
      setStageValidation({
        stage: WorkflowStage.STOCK_SOLUTIONS,
        validated: true,
        warnings: stockCalculationResult.warnings || [],
      })
    );

    navigate('/results', {
      state: { calculationData: reportBundle.reportData },
      replace: false,
    });
  };

  const canOpenPanel = (id: PanelId) => {
    if (id === 'parameters') return true;
    if (id === 'output') return configReady;
    if (id === 'verification') return Boolean(stockCalculationResult);
    return false;
  };

  return (
    <Box>
      <WorkflowLayout
        stage={WorkflowStage.STOCK_SOLUTIONS}
        title="Recipe"
        description="Set parameters, solve the matrix, verify balance, export results."
      >
        <DssContextStrip
          stockVolumeL={stockVolume}
          volumeUnit={volumeUnit}
          concentrationFactor={concentrationFactor}
          degreeOfFreedom={degreeOfFreedomElement}
        />

        {/* Three DSS panels only */}
        <Box
          role="tablist"
          aria-label="Recipe panels"
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}
        >
          {PANELS.map((p, i) => {
            const open = canOpenPanel(p.id);
            const current = panel === p.id;
            const done =
              (p.id === 'parameters' && configReady) ||
              (p.id === 'output' && Boolean(stockCalculationResult)) ||
              (p.id === 'verification' && Boolean(stockCalculationResult));
            return (
              <Chip
                key={p.id}
                role="tab"
                aria-selected={current}
                label={`${i + 1}. ${p.label}`}
                size="small"
                color={current ? 'primary' : done ? 'success' : 'default'}
                variant={current || done ? 'filled' : 'outlined'}
                onClick={() => open && setPanel(p.id)}
                sx={{ cursor: open ? 'pointer' : 'not-allowed', opacity: open ? 1 : 0.45 }}
              />
            );
          })}
        </Box>

        {!(saltSelection && formulation && waterProfile) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Confirm water, crop, and salts before calculating.
          </Alert>
        )}

        {/* —— PARAMETERS —— */}
        {panel === 'parameters' && (
          <Box>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Stock parameters
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={stockType}
                        label="Type"
                        onChange={(e) =>
                          setStockType(e.target.value as 'single' | 'partAB')
                        }
                      >
                        <MenuItem value="single">Single tank</MenuItem>
                        <MenuItem value="partAB">A + B tanks</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 6, md: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Volume"
                      type="number"
                      value={stockVolume}
                      onChange={(e) => setStockVolume(Number(e.target.value))}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 2 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Unit</InputLabel>
                      <Select
                        value={volumeUnit}
                        label="Unit"
                        onChange={(e) => setVolumeUnit(e.target.value)}
                      >
                        <MenuItem value="L">L</MenuItem>
                        <MenuItem value="gal">gal</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 6, md: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="CF (1:X)"
                      type="number"
                      value={concentrationFactor}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setConcentrationFactor(v);
                        setDilutionRatio(v);
                        setWorkingSolutionVolume(stockVolume * Math.max(1, v));
                      }}
                      inputProps={{ min: 1 }}
                      helperText="mL/L final = 1000/CF"
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 2 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>DOF</InputLabel>
                      <Select
                        value={degreeOfFreedomElement}
                        label="DOF"
                        onChange={(e) =>
                          setDegreeOfFreedomElement(e.target.value)
                        }
                      >
                        {DOF_OPTIONS.map((el) => (
                          <MenuItem key={el} value={el}>
                            {el}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <SaltCompatibilityAnalyzer
              salts={
                calcContext.salts.length > 0
                  ? calcContext.salts
                  : saltSelection?.selectedSalts || []
              }
              onCompatibilityAnalysis={handleCompatibilityAnalysis}
            />

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2, mb: 1 }}>
              <Button
                variant="contained"
                endIcon={<CalculateIcon />}
                disabled={!configReady}
                onClick={() => setPanel('output')}
              >
                Continue to solve
              </Button>
            </Stack>
            {!configReady && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Need water, crop targets, salts with composition, volume/CF, and tank
                logistics analysis.
              </Alert>
            )}
          </Box>
        )}

        {/* —— OUTPUT —— */}
        {panel === 'output' && (
          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
              <Button
                size="small"
                startIcon={<BackIcon />}
                onClick={() => setPanel('parameters')}
              >
                Parameters
              </Button>
            </Stack>

            <ProfessionalStockCalculator
              input={{
                salts:
                  calcContext.salts.length > 0
                    ? calcContext.salts
                    : saltSelection?.selectedSalts || [],
                stockType,
                stockVolume: calcContext.stockVolumeL || stockVolume,
                volumeUnit,
                concentrationFactor: calcContext.concentrationFactor,
                temperature: calcContext.temperatureC,
                degreeOfFreedomElement: calcContext.degreeOfFreedomElement,
                waterQuality: {
                  elements: calcContext.water.elements,
                  ph: calcContext.water.ph,
                  ec: calcContext.water.ec,
                },
                targetConcentrations: contextToTargetMap(calcContext),
              }}
              onCalculationComplete={handleStockCalculationComplete}
            />

            {stockCalculationResult && stockType === 'partAB' && (
              <Accordion variant="outlined" sx={{ mt: 2 }} defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">A/B tank assignment</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ABTankSeparationEngine
                    salts={stockCalculationResult.saltAmounts.map(
                      (saltAmount: any) => {
                        const originalSalt = saltSelection?.selectedSalts.find(
                          (s: any) => s.id === saltAmount.id
                        );
                        return {
                          ...(originalSalt || {
                            id: saltAmount.id,
                            name: saltAmount.name,
                            formula: saltAmount.formula || '',
                            elements: [],
                          }),
                          amount: saltAmount.amount,
                          unit: saltAmount.unit,
                          elements: originalSalt?.elements || saltAmount.elements || [],
                        };
                      }
                    )}
                    stockVolume={stockVolume}
                    onSeparationComplete={handleABSeparationComplete}
                  />
                </AccordionDetails>
              </Accordion>
            )}

            {stockCalculationResult && (
              <Accordion variant="outlined" sx={{ mt: 1 }} defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">pH adjustment (optional)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <HendersonHasselbalchCalculator
                    currentPH={
                      stockCalculationResult.solutionProperties?.finalPH || 6.5
                    }
                    solutionVolume={stockVolume}
                    ionicStrength={
                      stockCalculationResult.solutionProperties?.ionicStrength ||
                      0.01
                    }
                    dilutionRatio={concentrationFactor || dilutionRatio}
                    onPHAdjustmentCalculated={handlePHAdjustmentCalculated}
                  />
                </AccordionDetails>
              </Accordion>
            )}

            {stockCalculationResult && (
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  endIcon={<NextIcon />}
                  onClick={() => setPanel('verification')}
                >
                  Review & verify
                </Button>
              </Stack>
            )}
          </Box>
        )}

        {/* —— VERIFICATION —— */}
        {panel === 'verification' && stockCalculationResult && reportBundle && (
          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
              <Button
                size="small"
                startIcon={<BackIcon />}
                onClick={() => setPanel('output')}
              >
                Output
              </Button>
              <Button
                variant="contained"
                startIcon={<CheckIcon />}
                endIcon={<NextIcon />}
                onClick={handleFinalize}
              >
                Finalize → Results
              </Button>
            </Stack>

            <ProfessionalNutrientReport data={reportBundle.reportData} />

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3, mb: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckIcon />}
                endIcon={<NextIcon />}
                onClick={handleFinalize}
              >
                Finalize recipe & open results
              </Button>
            </Stack>
          </Box>
        )}

        {panel === 'verification' && !stockCalculationResult && (
          <Alert severity="info">
            Run the matrix solve in Output first.
            <Button size="small" sx={{ ml: 1 }} onClick={() => setPanel('output')}>
              Go to Output
            </Button>
          </Alert>
        )}
      </WorkflowLayout>
    </Box>
  );
};

export default StockSolutionsWorkflowPage;
